import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Anchor } from "lucide-react";
import { api, fmtErr, tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Public landing page for a supplier team invite link:
// /supplier-invite?token=... — shows the company, lets the invitee set a
// password (or enter their existing one) and signs them straight in.
export default function SupplierInviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/supplier/team/invite-info", { params: { token } });
        setInfo(data);
        if (data?.name) setName(data.name);
      } catch {
        setInfo({ valid: false, reason: "This invite link is invalid." });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/supplier/team/accept", { token, password, name });
      if (data?.access_token) tokenStore.set(data.access_token);
      await checkAuth();
      toast.success("Welcome aboard!");
      navigate("/app/supplier", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-8" data-testid="supplier-invite-page">
        <div className="flex items-center gap-2 mb-6 text-zinc-900">
          <Anchor className="h-5 w-5" />
          <span className="font-display text-lg">Seavanto</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Checking your invite…</div>
        ) : !info?.valid ? (
          <div data-testid="invite-invalid">
            <h1 className="font-display text-2xl mb-2">Invite not available</h1>
            <p className="text-sm text-zinc-500 mb-6">{info?.reason}</p>
            <Link to="/login" className="text-sm underline">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="font-display text-2xl mb-1">Join {info.company_name}</h1>
            <p className="text-sm text-zinc-500 mb-6">
              You've been invited as an account manager. {info.existing_account
                ? "You already have a Seavanto account with this email — enter its password to link it."
                : "Set a password to create your account."}
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="acc-email">Email</Label>
                <Input id="acc-email" value={info.email} readOnly className="bg-zinc-50" data-testid="accept-email-field" />
              </div>
              {!info.existing_account && (
                <div>
                  <Label htmlFor="acc-name">Your name</Label>
                  <Input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name-input" />
                </div>
              )}
              <div>
                <Label htmlFor="acc-pass">{info.existing_account ? "Your Seavanto password" : "Choose a password"}</Label>
                <Input
                  id="acc-pass" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={info.existing_account ? "Your existing password" : "At least 8 characters"}
                  data-testid="accept-password-input"
                />
              </div>
              <Button type="submit" disabled={busy || password.length < (info.existing_account ? 1 : 8)} className="w-full bg-ink hover:bg-black" data-testid="accept-invite-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {info.existing_account ? "Link account & sign in" : "Create account & sign in"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
